import { useState, useEffect, useCallback } from 'react';
import { config } from '../config';

const useAICredits = (workspaceId = null) => {
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCredits = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (workspaceId) {
        params.append('workspaceId', workspaceId);
      }

      const response = await fetch(`${config.API_URI}/v1/ai/credits/status?${params}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch credit status');
      }

      const data = await response.json();
      if (data.success) {
        setCredits(data.credits);
      } else {
        throw new Error(data.error || 'Failed to load credits');
      }
    } catch (err) {
      console.error('Credit status error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  const fetchHistory = useCallback(async (limit = 50, offset = 0) => {
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString()
      });

      if (workspaceId) {
        params.append('workspaceId', workspaceId);
      }

      const response = await fetch(`${config.API_URI}/v1/ai/credits/history?${params}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch credit history');
      }

      const data = await response.json();
      if (data.success) {
        return data.history;
      } else {
        throw new Error(data.error || 'Failed to load credit history');
      }
    } catch (err) {
      console.error('Credit history error:', err);
      throw err;
    }
  }, [workspaceId]);

  const migrateCredits = useCallback(async () => {
    try {
      const response = await fetch(`${config.API_URI}/v1/ai/credits/migrate`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to migrate credits');
      }

      const data = await response.json();
      if (data.success) {
        // Refresh credits after migration
        await fetchCredits();
        return data;
      } else {
        throw new Error(data.error || 'Failed to migrate credits');
      }
    } catch (err) {
      console.error('Credit migration error:', err);
      throw err;
    }
  }, [fetchCredits]);

  // Helper functions
  const hasWorkspaceCredits = useCallback(() => {
    return credits?.workspace && credits.workspace.credits > 0;
  }, [credits]);

  const hasPersonalCredits = useCallback(() => {
    return credits?.personal && credits.personal.credits > 0;
  }, [credits]);

  const hasAnyCredits = useCallback(() => {
    return hasWorkspaceCredits() || hasPersonalCredits();
  }, [hasWorkspaceCredits, hasPersonalCredits]);

  const getWorkspaceUsagePercentage = useCallback(() => {
    if (!credits?.workspace || !credits.workspace.maxCredits) return 0;
    const used = credits.workspace.maxCredits - credits.workspace.credits;
    return Math.round((used / credits.workspace.maxCredits) * 100);
  }, [credits]);

  const isWorkspaceUsageHigh = useCallback((threshold = 80) => {
    return getWorkspaceUsagePercentage() >= threshold;
  }, [getWorkspaceUsagePercentage]);

  const isPersonalCreditsLow = useCallback((threshold = 5) => {
    return credits?.personal && credits.personal.credits <= threshold;
  }, [credits]);

  const shouldShowWarning = useCallback(() => {
    return isWorkspaceUsageHigh() || isPersonalCreditsLow();
  }, [isWorkspaceUsageHigh, isPersonalCreditsLow]);

  const getWarningMessage = useCallback(() => {
    const warnings = [];

    if (isWorkspaceUsageHigh()) {
      const percentage = getWorkspaceUsagePercentage();
      warnings.push(`Workspace credits ${percentage}% used`);
    }

    if (isPersonalCreditsLow()) {
      warnings.push(`Only ${credits.personal.credits} personal credits remaining`);
    }

    return warnings.join(', ');
  }, [isWorkspaceUsageHigh, isPersonalCreditsLow, getWorkspaceUsagePercentage, credits]);

  const getTotalAvailableCredits = useCallback(() => {
    let total = 0;
    if (credits?.workspace) total += credits.workspace.credits;
    if (credits?.personal) total += credits.personal.credits;
    return total;
  }, [credits]);

  const getNextCreditSource = useCallback(() => {
    if (hasWorkspaceCredits()) return 'workspace';
    if (hasPersonalCredits()) return 'personal';
    return null;
  }, [hasWorkspaceCredits, hasPersonalCredits]);

  const canConsumeCredits = useCallback((amount = 1) => {
    return getTotalAvailableCredits() >= amount;
  }, [getTotalAvailableCredits]);

  // Update credits when response includes credit information
  const updateCreditsFromResponse = useCallback((responseData) => {
    if (responseData?.credits?.remaining) {
      setCredits(prevCredits => ({
        ...prevCredits,
        personal: {
          ...prevCredits?.personal,
          credits: responseData.credits.remaining.personal || 0
        },
        workspace: prevCredits?.workspace ? {
          ...prevCredits.workspace,
          credits: responseData.credits.remaining.workspace || 0
        } : null
      }));
    }
  }, []);

  useEffect(() => {
    fetchCredits();
  }, [fetchCredits]);

  return {
    // State
    credits,
    loading,
    error,

    // Actions
    fetchCredits,
    fetchHistory,
    migrateCredits,
    updateCreditsFromResponse,

    // Helper functions
    hasWorkspaceCredits,
    hasPersonalCredits,
    hasAnyCredits,
    getWorkspaceUsagePercentage,
    isWorkspaceUsageHigh,
    isPersonalCreditsLow,
    shouldShowWarning,
    getWarningMessage,
    getTotalAvailableCredits,
    getNextCreditSource,
    canConsumeCredits
  };
};

export default useAICredits;
